/* eslint-disable react-hooks/exhaustive-deps */
import { Currencies, WalletTypes } from '@constants/enums';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import DataLoadingWrapper from '../../../../components/common/DataLoadingWrapper';
import { COUNTRY_CURRENCY } from '../../../../components/common/Form/config';
import BalanceSkeleton from '../../../../components/common/Skeleton/BalanceSkeleton';
import WalletsSkeleton from '../../../../components/common/Skeleton/WalletsSkeleton';
import { formatCurrency } from '../../../../utils/formater';
import TabsWallet from '../tabsWallets';
import { TabsEfex } from '../tabsWallets/components/TabsEfex';
import WalletActions from './components/walletActions';
import ConvertModal from './ConvertModal';
import useWallets from './hooks/useWallets';
import { WalletsStyles } from './wallets.styles';

interface IWalletsProps {
  onDeposit: () => void;
  onPayment: () => void;
  onAddFundsCurrency: (currency: Currencies, walletType?: WalletTypes) => void;
}

const Wallets = ({
  onDeposit,
  onPayment,
  onAddFundsCurrency,
}: IWalletsProps) => {
  const { t } = useTranslation();

  const {
    userProfile,
    dataMixed,
    loadingBalances,
    loadingBalancesError,
    fixedGeneralBalance,
    originCurrency,
    filteredWalletData,
    activeTabIndices,
    activeTabLabels,
    isConvertModalOpen,
    currentTab,
    handleOnChangeTabs,
    handleAddFunds,
    showConvertModal,
    hideConvertModal,
    reloadWallet,
  } = useWallets({
    onAddFundsCurrency,
  });

  return (
    <>
      <Box className={WalletsStyles.wrapper}>
        <div className={WalletsStyles.balanceData}>
          <Typography className={WalletsStyles.balanceDataHeader} variant="h6">
            {t('walletTitle')}
          </Typography>
          <DataLoadingWrapper
            loadingData={loadingBalances}
            customLoader={<BalanceSkeleton />}
          >
            {!loadingBalancesError ? (
              <Typography
                variant="body1"
                className={WalletsStyles.balanceDataBalance}
              >
                {formatCurrency(fixedGeneralBalance)}{' '}
                <span>{originCurrency}</span>
              </Typography>
            ) : (
              <BalanceSkeleton />
            )}
          </DataLoadingWrapper>
          <Typography
            variant="caption"
            className="py-1 text-[#808080] inline-block w-full line-clamp-2"
          >
            {t('walletDisclaimer', {
              currency: COUNTRY_CURRENCY[
                userProfile?.company.country as keyof typeof COUNTRY_CURRENCY
              ] as Currencies,
              date: DateTime.now().toLocaleString(DateTime.DATETIME_MED),
            })}
          </Typography>
        </div>
        <WalletActions
          onDeposit={onDeposit}
          onPayment={onPayment}
          showConvertModal={showConvertModal}
        />
      </Box>
      <Box className={WalletsStyles.walletsDataContainer}>
        <DataLoadingWrapper
          loadingData={!dataMixed.walletsDataMixed?.length}
          customLoader={<WalletsSkeleton />}
        >
          <Box className={WalletsStyles.tabsWrapper}>
            <TabsEfex
              currentTab={currentTab}
              tabIndices={activeTabIndices}
              tabLabels={activeTabLabels}
              onChangeTabs={handleOnChangeTabs}
            />
          </Box>
          <TabsWallet
            onConvert={showConvertModal}
            data={filteredWalletData}
            addFunds={handleAddFunds}
            currentTab={currentTab}
          />
        </DataLoadingWrapper>
      </Box>
      {isConvertModalOpen && dataMixed.walletsDataMixed && (
        <ConvertModal
          open={isConvertModalOpen}
          wallets={dataMixed.walletsDataMixed}
          onClose={hideConvertModal}
          shouldReload={() => {
            hideConvertModal();
            reloadWallet();
          }}
        />
      )}
    </>
  );
};

export default Wallets;
